import { useEffect, useState } from "react"
import { countries } from "../../utils/countryData"

// EP1001 "Erfinder" form (SCRUM-55), aligned with the real EPO tool:
// separate first/last name, a "deceased" flag, a structured residence
// (postal code / city / country), the Rule 20(1) waiver, and — per
// inventor — the Rule 19(1)(d) statement of how the applicant acquired
// the right. An inventor who is also an applicant (added via "Applicant
// is also inventor") needs no acquisition statement.

type RightAcquisition = "EMPLOYER" | "LEGAL_SUCCESSOR" | "AGREEMENT"

const ACQUISITION_OPTIONS: { value: RightAcquisition; label: string }[] = [
  { value: "EMPLOYER", label: "As employer" },
  { value: "LEGAL_SUCCESSOR", label: "As legal successor" },
  { value: "AGREEMENT", label: "By agreement dated" }
]

export default function InventorForm({
  onCancel,
  onSave,
  initialValues
}: {
  onCancel: () => void
  onSave: (data: any) => void
  initialValues?: Record<string, any>
}) {
  const applicantIsInventor = Boolean(initialValues?.applicantIsInventor)

  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [deceased, setDeceased] = useState(false)
  const [postalCode, setPostalCode] = useState("")
  const [city, setCity] = useState("")
  const [country, setCountry] = useState("")
  const [waiverIndicator, setWaiverIndicator] = useState(false)
  const [rightAcquisition, setRightAcquisition] = useState<RightAcquisition | "">("")
  const [agreementDate, setAgreementDate] = useState("")
  const [errors, setErrors] = useState<Record<string, boolean>>({})

  useEffect(() => {
    const v = initialValues || {}
    const legacyParts = (v.name || "").trim().split(/\s+/)

    setFirstName(v.firstName || legacyParts.slice(0, -1).join(" ") || "")
    setLastName(v.lastName || (legacyParts.length > 1 ? legacyParts.slice(-1)[0] : "") || "")
    setDeceased(Boolean(v.deceased))
    setPostalCode(v.residence?.postalCode || "")
    setCity(v.residence?.city || "")
    setCountry(v.residence?.country || "")
    setWaiverIndicator(Boolean(v.waiverIndicator))
    setRightAcquisition(v.rightAcquisition || "")
    setAgreementDate(v.agreementDate || "")
    setErrors({})
  }, [initialValues])

  const clearError = (name: string) =>
    setErrors((prev) => ({ ...prev, [name]: false }))

  const handleSubmit = () => {
    const nextErrors: Record<string, boolean> = {}
    if (!firstName.trim()) nextErrors.firstName = true
    if (!lastName.trim()) nextErrors.lastName = true
    if (!city.trim()) nextErrors.city = true
    if (!country) nextErrors.country = true

    if (!applicantIsInventor) {
      if (!rightAcquisition) nextErrors.rightAcquisition = true
      if (rightAcquisition === "AGREEMENT" && !agreementDate) {
        nextErrors.agreementDate = true
      }
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      return
    }

    onSave({
      ...(initialValues?.applicantIsInventor ? { applicantIsInventor: true } : {}),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      deceased,
      residence: {
        postalCode: postalCode.trim() || undefined,
        city: city.trim(),
        country
      },
      waiverIndicator,
      rightAcquisition: applicantIsInventor ? undefined : rightAcquisition || undefined,
      agreementDate:
        !applicantIsInventor && rightAcquisition === "AGREEMENT"
          ? agreementDate
          : undefined
    })
  }

  const inputClass = (hasError: boolean) =>
    `w-full rounded-md border px-3 py-2 text-sm transition focus:border-dantzBlue focus:ring-1 focus:ring-dantzBlue ${
      hasError ? "border-red-500 bg-white" : "border-gray-300 bg-white"
    }`
  const labelClass = "text-sm font-medium text-gray-700"
  const sectionTitle =
    "mt-6 mb-3 border-b border-gray-200 pb-1 text-sm font-semibold text-gray-800 first:mt-0"

  return (
    <div className="space-y-2">
      <h4 className={sectionTitle}>Details</h4>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <label className={labelClass}>Last name *</label>
          <input
            type="text"
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value)
              clearError("lastName")
            }}
            className={inputClass(Boolean(errors.lastName))}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={labelClass}>First name *</label>
          <input
            type="text"
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value)
              clearError("firstName")
            }}
            className={inputClass(Boolean(errors.firstName))}
          />
        </div>
      </div>

      <label className="flex cursor-pointer items-center gap-2 pt-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={deceased}
          onChange={(e) => setDeceased(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-dantzBlue focus:ring-dantzBlue"
        />
        Inventor is deceased
      </label>

      <h4 className={sectionTitle}>Address</h4>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <label className={labelClass}>Postal code</label>
          <input
            type="text"
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
            className={inputClass(false)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={labelClass}>City *</label>
          <input
            type="text"
            value={city}
            onChange={(e) => {
              setCity(e.target.value)
              clearError("city")
            }}
            className={inputClass(Boolean(errors.city))}
          />
        </div>
      </div>

      <div className="flex flex-col gap-1 pt-2">
        <label className={labelClass}>Country/territory *</label>
        <select
          value={country}
          onChange={(e) => {
            setCountry(e.target.value)
            clearError("country")
          }}
          className={inputClass(Boolean(errors.country))}
        >
          <option value="">Select</option>
          {countries.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      <h4 className={sectionTitle}>Statement on acquired rights and waivers</h4>

      {applicantIsInventor ? (
        <p className="rounded-md bg-blue-50 px-3 py-2 text-sm text-gray-600">
          This inventor is also an applicant, so no statement of how the
          right to the patent was acquired is required.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          <span className="text-sm text-gray-700">
            The applicant acquired the right to the European patent:
          </span>
          {ACQUISITION_OPTIONS.map((option) => (
            <label
              key={option.value}
              className="flex flex-wrap items-center gap-2 text-sm text-gray-700"
            >
              <input
                type="radio"
                name="patent-inventor-right-acquisition"
                checked={rightAcquisition === option.value}
                onChange={() => {
                  setRightAcquisition(option.value)
                  clearError("rightAcquisition")
                }}
                className="h-4 w-4 border-gray-300 text-dantzBlue focus:ring-dantzBlue"
              />
              {option.label}
              {option.value === "AGREEMENT" && rightAcquisition === "AGREEMENT" && (
                <input
                  type="date"
                  value={agreementDate}
                  onChange={(e) => {
                    setAgreementDate(e.target.value)
                    clearError("agreementDate")
                  }}
                  className={`ml-1 rounded-md border px-2 py-1 text-sm ${
                    errors.agreementDate ? "border-red-500" : "border-gray-300"
                  }`}
                />
              )}
            </label>
          ))}
          {errors.rightAcquisition && (
            <span className="text-xs text-red-600">Please choose one option.</span>
          )}
          {errors.agreementDate && (
            <span className="text-xs text-red-600">
              Enter the date of the agreement.
            </span>
          )}
        </div>
      )}

      <label className="flex cursor-pointer items-start gap-2 pt-3 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={waiverIndicator}
          onChange={(e) => setWaiverIndicator(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-dantzBlue focus:ring-dantzBlue"
        />
        The inventor waives the right to be mentioned (Rule 20(1) EPC)
      </label>

      <div className="flex flex-wrap gap-3 pt-5">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          className="rounded-lg bg-dantzBlue px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700"
        >
          Save
        </button>
      </div>
    </div>
  )
}
